/**
 * PostHog Wrapper with Comprehensive Deduplication
 * 
 * This module provides a wrapper around PostHog that automatically deduplicates
 * ALL PostHog operations, including automatic tracking events.
 * 
 * Usage:
 *   import { useDeduplicatedPostHog } from '../utils/posthogWrapper';
 *   const posthog = useDeduplicatedPostHog();
 *   posthog.capture('event', { ... });
 *   posthog.setPersonProperties({ ... });
 *   posthog.identify(userId, properties);
 */

import { usePostHog } from 'posthog-js/react';
import { deduplicatedPostHogCall } from './posthogDeduplication';

/**
 * React hook that returns a deduplicated PostHog instance
 * All method calls are automatically deduplicated
 */
export function useDeduplicatedPostHog() {
  const posthog = usePostHog();
  
  if (!posthog) {
    return null;
  }
  
  // Return a proxy that intercepts all method calls
  return new Proxy(posthog, {
    get(target, prop: string | symbol) {
      const original = target[prop as keyof typeof target];
      
      // If it's a function, wrap it with deduplication
      if (typeof original === 'function') {
        return (...args: any[]) => {
          return deduplicatedPostHogCall(target, prop as string, ...args);
        };
      }
      
      // For non-function properties, return as-is
      return original;
    },
  });
}

/**
 * Direct wrapper function for PostHog instance (non-hook version)
 * Use this when you have a PostHog instance but can't use the hook
 * 
 * @param posthog - PostHog instance to wrap
 * @returns Wrapped PostHog instance with deduplication
 */
export function wrapPostHogWithDeduplication(posthog: any) {
  if (!posthog) {
    return null;
  }
  
  return new Proxy(posthog, {
    get(target, prop: string | symbol) {
      const original = target[prop as keyof typeof target];
      
      // If it's a function, wrap it with deduplication
      if (typeof original === 'function') {
        return (...args: any[]) => {
          return deduplicatedPostHogCall(target, prop as string, ...args);
        };
      }
      
      // For non-function properties, return as-is
      return original;
    },
  });
}

